import { AI_ACTIVE_CONVERSATION_PREFIX, AI_STORAGE_PREFIX, MESSAGE_STATUS } from "./constants";

/**
 * Pure conversation state helpers for the persistent AI assistant.
 * Conversations are stored on the backend (history); sessionStorage only
 * caches the active one so the panel paints instantly after a refresh.
 */

export const initialConversationState = {
  isOpen: false,
  threadId: null,
  messages: [],
  draft: "",
  sending: false,
  restoring: false,
  error: null,
  ownerUserId: null,
  conversationId: null,
  conversationTitle: null,
  // Set when the backend refuses a message because the conversation is full.
  serverLimitReached: false,
};

export function createMessage({
  id,
  role,
  content,
  status = MESSAGE_STATUS.SENT,
  createdAt = new Date().toISOString(),
  retryPayload = null,
  event = false,
}) {
  return {
    id: id || `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content: content || "",
    status,
    createdAt,
    retryPayload,
    // Draft-card results: shown in the chat, never counted toward the limit.
    event: Boolean(event),
  };
}

export function conversationReducer(state, action) {
  switch (action.type) {
    case "OPEN":
      return { ...state, isOpen: true };
    case "CLOSE":
      return { ...state, isOpen: false };
    case "TOGGLE":
      return { ...state, isOpen: !state.isOpen };
    case "SET_DRAFT":
      return { ...state, draft: action.draft ?? "" };
    case "SET_SENDING":
      return { ...state, sending: Boolean(action.sending) };
    case "SET_RESTORING":
      return { ...state, restoring: Boolean(action.restoring) };
    case "SET_ERROR":
      return { ...state, error: action.error ?? null };
    case "SET_THREAD":
      return { ...state, threadId: action.threadId || null };
    case "SET_MESSAGES":
      return { ...state, messages: Array.isArray(action.messages) ? action.messages : [] };
    case "APPEND_MESSAGE":
      return { ...state, messages: [...state.messages, action.message] };
    case "UPDATE_MESSAGE": {
      const { id, patch } = action;
      return {
        ...state,
        messages: state.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
      };
    }
    case "REMOVE_MESSAGE":
      return {
        ...state,
        messages: state.messages.filter((m) => m.id !== action.id),
      };
    case "NEW_CONVERSATION":
      // Fresh conversation. Earlier ones stay in history; nothing is deleted.
      // `carryDraft` keeps the user's unsent input so it is never lost.
      return {
        ...state,
        threadId: null,
        messages: [],
        draft: action.carryDraft ? state.draft : "",
        sending: false,
        error: null,
        conversationId: null,
        conversationTitle: null,
        serverLimitReached: false,
      };
    case "SET_CONVERSATION":
      return {
        ...state,
        conversationId: action.conversationId || null,
        conversationTitle:
          action.title !== undefined ? action.title || null : state.conversationTitle,
      };
    case "SET_SERVER_LIMIT":
      return { ...state, serverLimitReached: Boolean(action.reached) };
    case "LOAD_CONVERSATION":
      return {
        ...state,
        threadId: null,
        conversationId: action.conversationId,
        conversationTitle: action.title || null,
        messages: Array.isArray(action.messages) ? action.messages : [],
        serverLimitReached: Boolean(action.limitReached),
        draft: action.carryDraft ? state.draft : "",
        sending: false,
        error: null,
      };
    case "HYDRATE": {
      const payload = action.payload || {};
      return {
        ...state,
        threadId: payload.threadId || null,
        messages: Array.isArray(payload.messages) ? payload.messages : [],
        draft: typeof payload.draft === "string" ? payload.draft : "",
        ownerUserId: payload.ownerUserId ?? state.ownerUserId,
        conversationId: payload.conversationId || null,
        conversationTitle: payload.conversationTitle || null,
        error: null,
        sending: false,
      };
    }
    case "BIND_OWNER":
      return {
        ...initialConversationState,
        isOpen: state.isOpen,
        ownerUserId: action.ownerUserId || null,
      };
    case "RESET_FOR_SECURITY":
      return { ...initialConversationState };
    default:
      return state;
  }
}

export function storageKeyForUser(userId) {
  const id = String(userId || "").trim();
  if (!id) return null;
  return `${AI_STORAGE_PREFIX}:${id}`;
}

export function loadPersistedConversation(userId) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.ownerUserId !== String(userId)) return null;
    return {
      threadId: parsed.threadId || null,
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
      draft: typeof parsed.draft === "string" ? parsed.draft : "",
      conversationId: parsed.conversationId || null,
      conversationTitle: parsed.conversationTitle || null,
      ownerUserId: String(userId),
    };
  } catch {
    return null;
  }
}

export function persistConversation(userId, snapshot) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return;
  try {
    const payload = {
      ownerUserId: String(userId),
      threadId: snapshot.threadId || null,
      conversationId: snapshot.conversationId || null,
      conversationTitle: snapshot.conversationTitle || null,
      messages: (snapshot.messages || []).map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        status: m.status === MESSAGE_STATUS.FAILED ? MESSAGE_STATUS.FAILED : MESSAGE_STATUS.SENT,
        createdAt: m.createdAt,
        event: Boolean(m.event),
      })),
      draft: snapshot.draft || "",
      updatedAt: new Date().toISOString(),
    };
    window.sessionStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // Ignore quota / private-mode failures — in-memory state still works.
  }
}

export function clearPersistedConversation(userId) {
  const key = storageKeyForUser(userId);
  if (!key || typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Active conversation id survives refreshes and new tabs (per user). */
export function loadActiveConversationId(userId) {
  const id = String(userId || "").trim();
  if (!id || typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(`${AI_ACTIVE_CONVERSATION_PREFIX}:${id}`) || null;
  } catch {
    return null;
  }
}

export function persistActiveConversationId(userId, conversationId) {
  const id = String(userId || "").trim();
  if (!id || typeof window === "undefined") return;
  try {
    const key = `${AI_ACTIVE_CONVERSATION_PREFIX}:${id}`;
    if (conversationId) window.localStorage.setItem(key, conversationId);
    else window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/** Backend conversation messages -> assistant message shape. */
export function mapServerMessages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((m) =>
    createMessage({
      id: m.id,
      role: m.role === "assistant" ? "assistant" : "user",
      content: m.content,
      status: MESSAGE_STATUS.SENT,
      createdAt: m.created_at || m.createdAt,
      event: m.event,
    })
  );
}

/** Strip tutor metadata markers from AI HTML responses. */
const TUTOR_MARKER = "<!--microlab-tutor:";

export function sanitizeTutorContent(content) {
  const raw = String(content || "");
  const index = raw.lastIndexOf(TUTOR_MARKER);
  if (index === -1) return raw;
  return raw.slice(0, index).trim();
}

/**
 * Map backend thread messages into assistant message shape.
 * @param {Array} raw
 */
export function mapThreadMessages(raw) {
  if (!Array.isArray(raw)) return [];
  return [...raw]
    .reverse()
    .map((m) =>
      createMessage({
        id: m._id || m.id,
        role: m?.user?._id === "ai" || m?.sender === "ai" ? "assistant" : "user",
        content: sanitizeTutorContent(m?.text || m?.content || ""),
        status: MESSAGE_STATUS.SENT,
        createdAt: m?.createdAt || m?.created_at,
      })
    );
}
